import { useCallback, useMemo, useState } from 'react'
import { PlusCircle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getCategories } from '../../services/CategoryService'
import { useDeleteProduct, useProductsPaginated, useRestoreProduct } from '../../hooks/useProducts'
import ProductCard from '../../components/products/ProductCard'
import SearchBar from '../../components/products/SearchBar'
import ProductFormModal from '../../components/products/ProductFormModal'
import ProductViewModal from '../../components/products/ProductViewModal'
import ConfirmDeleteModal from '../../components/products/ConfirmDeleteModal'
import type { Product, ProductCardVM } from '../../types/product'

export default function ProductsPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isFormOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingProductId, setEditingProductId] = useState<string | number | null>(null)
  const [viewingProductId, setViewingProductId] = useState<string | number | null>(null)
  const [deleteProductId, setDeleteProductId] = useState<string | number | null>(null)
  const [showDeleted, setShowDeleted] = useState(false)

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(8)

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  })

  const { data: paginatedData, isPending, isError } = useProductsPaginated({
    page,
    perPage,
    q: debouncedSearch,
    showDeleted,
  })

  const products = paginatedData?.data ?? []
  const totalProducts = paginatedData?.meta?.total ?? 0
  const lastPage = paginatedData?.meta?.last_page ?? 1

  const deleteMutation = useDeleteProduct()
  const restoreMutation = useRestoreProduct()

  const handleDebouncedSearch = useCallback((val: string) => {
    setDebouncedSearch(val)
    setPage(1)
  }, [])

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = []
    const range = 1 // number of pages to show on either side of current page
    
    for (let i = 1; i <= lastPage; i++) {
      if (
        i === 1 ||
        i === lastPage ||
        (i >= page - range && i <= page + range)
      ) {
        pages.push(i)
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...')
      }
    }
    return pages
  }, [page, lastPage])

  const productCards = useMemo<ProductCardVM[]>(() => {
    const list = (Array.isArray(products) ? products : []) as Product[]
    return list.map((product) => ({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      categoryLabel: product.category?.name ?? 'Sin categoría',
      isActive: product.active,
      isFeatured: product.isFeatured,
      imageUrl: product.imageUrl,
      ingredients: product.ingredients.map((ingredient) => ingredient.name),
      deletedAt: product.deletedAt,
    }))
  }, [products])

  const openCreateModal = () => {
    setFormMode('create')
    setEditingProductId(null)
    setFormOpen(true)
  }

  const openEditModal = (id: string | number) => {
    setFormMode('edit')
    setEditingProductId(id)
    setFormOpen(true)
  }

  const closeFormModal = () => {
    setFormOpen(false)
    setEditingProductId(null)
  }

  const closeViewModal = () => {
    setViewingProductId(null)
  }

  const closeDeleteModal = () => {
    setDeleteProductId(null)
  }

  const handleConfirmDelete = async () => {
    if (!deleteProductId) return
    await deleteMutation.mutateAsync(deleteProductId)
    setDeleteProductId(null)
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-3">
            Productos
            {!isPending && !isError && (
              <span className="inline-flex items-center rounded-full bg-blue-550/10 text-blue-600 dark:text-blue-400 px-3 py-1 text-sm font-medium border border-blue-550/20">
                {totalProducts} en total
              </span>
            )}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Gestiona tu catálogo en modo claro u oscuro sin perder consistencia.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
        >
          <PlusCircle className="h-5 w-5" />
          Agregar producto
        </button>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center justify-between">
        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={setSearch}
            onDebouncedChange={handleDebouncedSearch}
            placeholder="Busca por nombre, descripción o ingrediente"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-neutral-600 dark:text-neutral-400 shrink-0">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => {
              setShowDeleted(e.target.checked)
              setPage(1)
            }}
            className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
          />
          <span>Mostrar eliminados / archivados</span>
        </label>
      </div>
      {isPending ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando productos…</p>
      ) : isError ? (
        <p className="text-sm text-rose-500">No pudimos cargar los productos. Intenta nuevamente.</p>
      ) : productCards.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No encontramos productos para tu búsqueda.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {productCards.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                highlight={debouncedSearch}
                onView={(id) => setViewingProductId(id)}
                onEdit={openEditModal}
                onDelete={(id) => setDeleteProductId(id)}
                onRestore={async (id) => {
                  try {
                    await restoreMutation.mutateAsync(id)
                  } catch (e) {
                    // toast handled by useRestoreProduct onError
                  }
                }}
              />
            ))}
          </div>

          {/* Controles de Paginación */}
          <div className="flex flex-col items-center justify-between gap-4 border-t border-neutral-200 pt-6 sm:flex-row dark:border-neutral-800">
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              Mostrando{' '}
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                {Math.min((page - 1) * perPage + 1, totalProducts)}
              </span>{' '}
              a{' '}
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                {Math.min(page * perPage, totalProducts)}
              </span>{' '}
              de{' '}
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">
                {totalProducts}
              </span>{' '}
              productos
            </div>

            <div className="flex items-center gap-4">
              {/* Selector de items por página */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500 dark:text-neutral-400">Por página:</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value))
                    setPage(1)
                  }}
                  className="rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm text-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
                >
                  <option value={4}>4</option>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                </select>
              </div>

              {/* Botones de navegación de página */}
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-500 transition-colors hover:bg-neutral-50 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
                >
                  &larr;
                </button>

                {pageNumbers.map((p, idx) => {
                  if (p === '...') {
                    return (
                      <span
                        key={`ellipsis-${idx}`}
                        className="inline-flex h-9 w-9 items-center justify-center text-sm text-neutral-500"
                      >
                        …
                      </span>
                    )
                  }
                  const isCurrent = p === page
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(Number(p))}
                      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold transition-colors ${
                        isCurrent
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                })}

                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(prev + 1, lastPage))}
                  disabled={page === lastPage}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-500 transition-colors hover:bg-neutral-50 disabled:pointer-events-none disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
                >
                  &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ProductFormModal
        mode={formMode}
        isOpen={isFormOpen}
        onClose={closeFormModal}
        productId={formMode === 'edit' ? editingProductId : undefined}
        categories={categories}
      />
      <ProductViewModal isOpen={Boolean(viewingProductId)} productId={viewingProductId} onClose={closeViewModal} />
      <ConfirmDeleteModal
        isOpen={Boolean(deleteProductId)}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        productName={products.find((product) => String(product.id) === String(deleteProductId))?.name}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  )
}
